import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, finalize } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { TransportSide } from '@/new-transport/_models/transport.model';
import { TermStopSalesApiService } from './term-stop-sales.api.service';
import { TermStopSalesDialogData, TermStopSalesDialogResult, TermTimetableRow } from './term-stop-sales-dialog.model';

@Component({
	selector: 'app-term-stop-sales-dialog',
	standalone: true,
	imports: [
		DatePipe,
		FormsModule,
		MatDialogModule,
		MatTableModule,
		MatSlideToggleModule,
		MatButtonModule,
		MatProgressSpinnerModule,
		NgClass,
	],
	templateUrl: './term-stop-sales-dialog.component.html',
	styleUrls: ['./term-stop-sales-dialog.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermStopSalesDialogComponent {
	readonly data = inject<TermStopSalesDialogData>(MAT_DIALOG_DATA);
	readonly displayedColumns = ['side', 'city', 'time', 'stopSale'] as const;
	readonly rows = signal<TermTimetableRow[]>([]);
	readonly isLoading = signal(true);
	readonly pendingIds = signal<ReadonlySet<number>>(new Set<number>());
	readonly blockedCount = computed(() => this.rows().filter((row) => row.stopSale).length);

	private readonly api = inject(TermStopSalesApiService);
	private readonly infobox = inject(InfoboxService);
	private readonly destroyRef = inject(DestroyRef);
	private readonly dialogRef =
		inject<MatDialogRef<TermStopSalesDialogComponent, TermStopSalesDialogResult>>(MatDialogRef);

	constructor() {
		this.api
			.getTermTimetables(this.data.termId)
			.pipe(
				catchError(() => {
					this.infobox.error('Nie udało się pobrać miast terminu');
					return EMPTY;
				}),
				finalize(() => this.isLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((rows) => this.rows.set(rows));
	}

	sideLabel(side: TransportSide): string {
		return side === TransportSide.DEPARTURE ? 'Wyjazd' : 'Powrót';
	}

	isPending(id: number): boolean {
		return this.pendingIds().has(id);
	}

	onToggle(row: TermTimetableRow, event: MatSlideToggleChange): void {
		const previous = row.stopSale;
		const desired = event.checked;
		this.applyStopSale(row.id, desired);
		this.markPending(row.id, true);
		this.api
			.patchTimetableStopSale(row.id, desired)
			.pipe(
				catchError(() => {
					this.applyStopSale(row.id, previous);
					this.infobox.error('Nie udało się zapisać StopSale dla miasta');
					return EMPTY;
				}),
				finalize(() => this.markPending(row.id, false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe(() => {
				this.infobox.success('Zapisano');
			});
	}

	close(): void {
		this.dialogRef.close({ blockedTimetableCount: this.blockedCount() });
	}

	private applyStopSale(id: number, value: boolean): void {
		this.rows.update((rows) => rows.map((row) => (row.id === id ? { ...row, stopSale: value } : row)));
	}

	private markPending(id: number, pending: boolean): void {
		this.pendingIds.update((set) => {
			const next = new Set(set);
			if (pending) {
				next.add(id);
			} else {
				next.delete(id);
			}
			return next;
		});
	}
}
